/**
 * Weekly AI teardown of competitor reels/posts — UI for
 * /api/insights/competitor-teardown.
 *
 * Reads top-3 posts per tracked competitor from the last 7d, computes
 * an engagement-vs-average multiple per post, ships it to Claude, and
 * renders the patterns + per-post breakdowns. Cached server-side for 7d.
 */
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink, Loader2, Play, RefreshCw, Rocket, Sparkles, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatNumberCompact } from '@/lib/format';
import { imgProxy } from '@/lib/img';
import { marketing } from '@/lib/supabase/marketing';
import { cn } from '@/lib/utils';
import type {
  IgCompetitor,
  IgCompetitorPost,
  IgCompetitorSnapshot,
} from '@/hooks/useCompetitors';
import type { PostPreviewData } from '@/components/PostPreview';

interface Props {
  competitors: IgCompetitor[];
  snapshots: IgCompetitorSnapshot[];
  posts: IgCompetitorPost[];
  onPreview: (post: PostPreviewData) => void;
}

interface Pattern {
  title: string;
  evidence: string;
  competitorsUsing: string[];
  replicaBrief: {
    hook: string;
    format: string;
    audioHint: string;
    shootLocation: string;
    captionDraft: string;
    publishSlot: string;
  };
}

interface PostAnalysis {
  postId: string;
  username: string;
  permalink: string;
  engagementMultiple: number;
  hook: string;
  format: string;
  visualHook: string;
  audio: string;
  captionStrategy: string;
  emotionalTrigger: string;
  valuePromise: string;
  replicability: 'high' | 'medium' | 'low';
  paidLift?: 'boosted' | 'organic';
  whyItWorked: string;
}

interface TeardownResponse {
  headline: string;
  patterns: Pattern[];
  postAnalyses: PostAnalysis[];
  cached?: boolean;
  generatedAt?: string;
  fallback?: string;
  error?: string;
  detail?: string;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function WhyItWorked({ competitors, snapshots, posts, onPreview }: Props) {
  const [force, setForce] = useState(false);

  const followersByHandle = useMemo(() => {
    // Latest snapshot per handle wins.
    const latest = new Map<string, IgCompetitorSnapshot>();
    for (const s of snapshots) {
      const prev = latest.get(s.username);
      if (!prev || (s.snapshot_date ?? '') > (prev.snapshot_date ?? '')) latest.set(s.username, s);
    }
    const map = new Map<string, number>();
    for (const [h, s] of latest) map.set(h, s.followers_count ?? 0);
    return map;
  }, [snapshots]);

  const payload = useMemo(
    () => buildPayload(competitors, followersByHandle, posts),
    [competitors, followersByHandle, posts],
  );
  const postById = useMemo(() => {
    const map = new Map<string, IgCompetitorPost>();
    for (const p of posts) map.set(p.post_id, p);
    return map;
  }, [posts]);

  const teardown = useMutation({
    mutationFn: async (): Promise<TeardownResponse> => {
      // Supabase Edge Function has the headroom CF Pages free tier lacks
      // (60s wall, no CPU ceiling). supabase-js attaches the anon key
      // automatically; verify_jwt=true on the function checks it.
      // Append ?force=1 when the user explicitly refreshes — bypasses the
      // server-side 7-day cache so Claude gets re-called.
      const { data, error } = await marketing.functions.invoke<TeardownResponse>(
        force ? 'competitor-teardown?force=1' : 'competitor-teardown',
        { body: payload },
      );
      if (error) throw new Error(error.message);
      if (!data) throw new Error('empty response');
      if (data.error) throw new Error(data.error);
      return data;
    },
  });

  if (payload.competitors.length === 0) {
    return (
      <div className="rounded-md border border-border/40 bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        No posts from the last 7 days yet. Sync competitor data first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="rounded-md border border-border/40 bg-gradient-to-br from-violet-500/5 to-amber-500/5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-2xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-violet-500" />
              Weekly AI teardown · {payload.weekStart} → {payload.weekEnd}
            </div>
            <h3 className="mt-1 text-lg font-semibold leading-tight">
              {teardown.data?.headline ?? 'Why their reels worked this week'}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {payload.competitors.length} competitors ·{' '}
              {payload.competitors.reduce((a, c) => a + c.topPosts.length, 0)} top posts analyzed
              {teardown.data?.generatedAt && (
                <>
                  {' '}
                  · cached{' '}
                  {new Date(teardown.data.generatedAt).toLocaleDateString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              // First generate hits cache; subsequent Refresh clicks force regen.
              setForce(Boolean(teardown.data));
              teardown.mutate();
            }}
            disabled={teardown.isPending}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium',
              'hover:bg-accent disabled:opacity-50',
            )}
          >
            {teardown.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Analyzing…
              </>
            ) : teardown.data ? (
              <>
                <RefreshCw className="h-3 w-3" />
                Refresh
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3 text-violet-500" />
                Generate
              </>
            )}
          </button>
        </div>

        {teardown.isError && (
          <div className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-2xs text-rose-600">
            {(teardown.error as Error).message}
          </div>
        )}
      </div>

      {/* Patterns */}
      {teardown.data?.patterns && teardown.data.patterns.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Playbook patterns — replicate these
          </h4>
          <div className="grid gap-3 lg:grid-cols-2">
            {teardown.data.patterns.map((p, idx) => (
              <PatternCard key={idx} pattern={p} />
            ))}
          </div>
        </div>
      )}

      {/* Per-post analyses */}
      {teardown.data?.postAnalyses && teardown.data.postAnalyses.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Per-post breakdowns
          </h4>
          <div className="space-y-2">
            {teardown.data.postAnalyses.map((a) => {
              const post = postById.get(a.postId);
              return (
                <PostAnalysisRow
                  key={a.postId}
                  analysis={a}
                  post={post ?? null}
                  onPreview={onPreview}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback prose if JSON parse failed */}
      {teardown.data?.fallback && (
        <div className="rounded-md border border-border/40 bg-card p-4 text-sm whitespace-pre-wrap">
          {teardown.data.fallback}
        </div>
      )}
    </div>
  );
}

function PatternCard({ pattern }: { pattern: Pattern }) {
  const brief = pattern.replicaBrief;
  return (
    <div className="rounded-md border border-border/40 bg-card p-4">
      <div className="flex items-start gap-2">
        <Trophy className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <h5 className="text-sm font-semibold leading-tight">{pattern.title}</h5>
          <p className="mt-1 text-xs text-muted-foreground">{pattern.evidence}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {pattern.competitorsUsing.map((h) => (
              <Badge key={h} variant="muted" className="text-2xs">
                {h}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {brief && (
        <div className="mt-3 rounded-md border border-violet-500/20 bg-violet-500/5 p-3">
          <div className="text-2xs font-medium uppercase tracking-wide text-violet-600 dark:text-violet-400">
            Flax replica brief
          </div>
          <dl className="mt-2 space-y-1.5 text-xs">
            <BriefRow label="Hook" value={brief.hook} />
            <BriefRow label="Format" value={brief.format} />
            <BriefRow label="Audio" value={brief.audioHint} />
            <BriefRow label="Location" value={brief.shootLocation} />
            <BriefRow label="When" value={brief.publishSlot} />
          </dl>
          {brief.captionDraft && (
            <div className="mt-2 rounded border border-border/40 bg-background/60 p-2">
              <div className="text-2xs uppercase tracking-wide text-muted-foreground">
                Caption draft
              </div>
              <p className="mt-0.5 text-xs leading-relaxed">{brief.captionDraft}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-16 shrink-0 text-2xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-xs leading-relaxed">{value}</dd>
    </div>
  );
}

function PostAnalysisRow({
  analysis,
  post,
  onPreview,
}: {
  analysis: PostAnalysis;
  post: IgCompetitorPost | null;
  onPreview: (p: PostPreviewData) => void;
}) {
  const isVideo = post?.media_type === 'VIDEO';
  const openPreview = () => {
    if (!post) return;
    onPreview({
      id: post.post_id,
      caption: post.caption,
      permalink: post.permalink,
      imageUrl: post.image_url,
      thumbnailUrl: post.thumbnail_url,
      r2ThumbKey: post.r2_thumb_key,
      r2MediaKey: post.r2_media_key,
      mediaType: post.media_type,
      postedAt: post.posted_at,
      likes: post.like_count,
      comments: post.comments_count,
      views: post.views,
      isBoosted: post.is_boosted,
      source: `@${post.username}`,
    });
  };

  const replicaColor =
    analysis.replicability === 'high'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : analysis.replicability === 'medium'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
        : 'bg-rose-500/15 text-rose-700 dark:text-rose-400';

  return (
    <div className="flex gap-3 rounded-md border border-border/40 bg-card p-3">
      <button
        type="button"
        onClick={openPreview}
        disabled={!post}
        className="relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/40 bg-muted disabled:opacity-50"
      >
        {post && isVideo && post.r2_media_key ? (
          <>
            <video
              src={imgProxy(null, post.r2_media_key)}
              preload="metadata"
              muted
              playsInline
              className="h-full w-full object-cover"
            />
            <Play
              className="absolute h-5 w-5 text-white drop-shadow-lg"
              fill="currentColor"
              aria-hidden
            />
          </>
        ) : post && (post.r2_thumb_key || (post.image_url && !post.image_url.match(/\.mp4/i))) ? (
          <img
            src={imgProxy(post.image_url ?? post.thumbnail_url, post.r2_thumb_key ?? post.r2_media_key)}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <Play className="h-6 w-6 text-muted-foreground" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
          <span className="font-medium text-foreground">@{analysis.username}</span>
          <Badge variant="muted" className="text-2xs">
            {analysis.engagementMultiple.toFixed(1)}× their avg
          </Badge>
          <span className={cn('rounded px-1.5 py-0.5 text-2xs font-medium', replicaColor)}>
            {analysis.replicability} replicability
          </span>
          {(analysis.paidLift === 'boosted' || post?.is_boosted) && (
            <span
              className="inline-flex items-center gap-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-2xs font-medium text-sky-700 dark:text-sky-400"
              title="Currently boosted with paid spend — engagement is paid + organic"
            >
              <Rocket className="h-3 w-3" />
              Boosted
            </span>
          )}
          {post?.like_count != null && (
            <span>· {formatNumberCompact(post.like_count)} likes</span>
          )}
          {analysis.permalink && (
            <a
              href={analysis.permalink}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-2xs text-muted-foreground hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              IG <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        <p className="mt-1.5 text-sm font-medium leading-snug">{analysis.whyItWorked}</p>

        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-2xs sm:grid-cols-2">
          <Field label="Hook" value={analysis.hook} />
          <Field label="Format" value={analysis.format} />
          <Field label="Visual" value={analysis.visualHook} />
          <Field label="Audio" value={analysis.audio} />
          <Field label="Caption" value={analysis.captionStrategy} />
          <Field label="Trigger" value={`${analysis.emotionalTrigger} · ${analysis.valuePromise}`} />
        </dl>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="shrink-0 text-muted-foreground">{label}:</dt>
      <dd className="min-w-0 text-foreground/90">{value}</dd>
    </div>
  );
}

/**
 * Build the request payload: top 3 posts per competitor from the last 7 days,
 * each with an engagementMultiple computed against that account's own avg
 * engagement (so a small brand's banger ranks fairly against a big brand's
 * average).
 */
function buildPayload(
  competitors: IgCompetitor[],
  followersByHandle: Map<string, number>,
  posts: IgCompetitorPost[],
) {
  const since = Date.now() - SEVEN_DAYS_MS;
  const weekEnd = new Date().toISOString().slice(0, 10);
  const weekStart = new Date(since).toISOString().slice(0, 10);

  const byHandle = new Map<string, IgCompetitorPost[]>();
  for (const p of posts) {
    if (!p.posted_at) continue;
    if (new Date(p.posted_at).getTime() < since) continue;
    const arr = byHandle.get(p.username) ?? [];
    arr.push(p);
    byHandle.set(p.username, arr);
  }

  const competitorsOut = competitors
    .filter((c) => byHandle.has(c.username))
    .map((c) => {
      const rows = byHandle.get(c.username) ?? [];
      const total = rows.reduce(
        (s, r) => s + (r.like_count ?? 0) + (r.comments_count ?? 0),
        0,
      );
      const avg = rows.length > 0 ? total / rows.length : 0;
      const ranked = [...rows]
        .map((r) => {
          const eng = (r.like_count ?? 0) + (r.comments_count ?? 0);
          const mult = avg > 0 ? eng / avg : 1;
          return { row: r, eng, mult };
        })
        .sort((a, b) => b.eng - a.eng)
        .slice(0, 3);

      return {
        username: c.username,
        followers: followersByHandle.get(c.username) ?? 0,
        topPosts: ranked.map(({ row, mult }) => ({
          postId: row.post_id,
          username: row.username,
          postedAt: row.posted_at,
          mediaType: row.media_type,
          caption: row.caption,
          permalink: row.permalink,
          likes: row.like_count ?? 0,
          comments: row.comments_count ?? 0,
          views: row.views ?? 0,
          engagementMultiple: mult,
          isBoosted: Boolean(row.is_boosted),
        })),
      };
    })
    .filter((c) => c.topPosts.length > 0);

  return { weekStart, weekEnd, competitors: competitorsOut };
}
